import { ClientDisplayableError, isClientDisplayableError, isErrorResponse, } from '@standardnotes/responses';
import { ContentType, NotificationType, Result } from '@standardnotes/domain-core';
import { PayloadEmitSource, } from '@standardnotes/models';
import { SyncEvent } from './../Event/SyncEvent';
import { AbstractService } from './../Service/AbstractService';
import { VaultInviteServiceEvent } from './VaultInviteServiceEvent';
import { ApplicationEvent } from '../Event/ApplicationEvent';
import { WebSocketsServiceEvent } from '../Api/WebSocketsServiceEvent';
import { NotificationServiceEvent } from '../UserEvent/NotificationServiceEvent';
export class VaultInviteService extends AbstractService {
    constructor(items, session, vaultUsers, sync, invitesServer, status, _getAllContacts, _getVault, _getVaultContacts, _inviteToVault, _getTrustedPayload, _getUntrustedPayload, _findContact, _acceptVaultInvite, _getKeyPairs, _decryptErroredPayloads, eventBus) {
        super(eventBus);
        this.session = session;
        this.vaultUsers = vaultUsers;
        this.sync = sync;
        this.invitesServer = invitesServer;
        this.status = status;
        this._getAllContacts = _getAllContacts;
        this._getVault = _getVault;
        this._getVaultContacts = _getVaultContacts;
        this._inviteToVault = _inviteToVault;
        this._getTrustedPayload = _getTrustedPayload;
        this._getUntrustedPayload = _getUntrustedPayload;
        this._findContact = _findContact;
        this._acceptVaultInvite = _acceptVaultInvite;
        this._getKeyPairs = _getKeyPairs;
        this._decryptErroredPayloads = _decryptErroredPayloads;
        this.pendingInvites = {};
        this.eventDisposers.push(items.addObserver(ContentType.TYPES.TrustedContact, async ({ inserted, source }) => {
            if (source === PayloadEmitSource.LocalChanged && inserted.length > 0) {
                void this.downloadInboundInvites();
            }
            await this.reprocessCachedInvitesTrustStatusAfterTrustedContactsChange();
        }));
    }
    deinit() {
        super.deinit();
        this.session = undefined;
        this.vaultUsers = undefined;
        this.sync = undefined;
        this.invitesServer = undefined;
        this._getAllContacts = undefined;
        this._getVault = undefined;
        this._getVaultContacts = undefined;
        this._inviteToVault = undefined;
        this._getTrustedPayload = undefined;
        this._getUntrustedPayload = undefined;
        this._findContact = undefined;
        this._acceptVaultInvite = undefined;
        this._getKeyPairs = undefined;
        this._decryptErroredPayloads = undefined;
        this.pendingInvites = {};
    }
    updatePendingInviteCount() {
        this.status.setPreferencesBubbleCount('vaults', Object.keys(this.pendingInvites).length);
    }
    addPendingInvite(invite) {
        this.pendingInvites[invite.invite.uuid] = invite;
        this.updatePendingInviteCount();
    }
    removePendingInvite(uuid) {
        delete this.pendingInvites[uuid];
        this.updatePendingInviteCount();
    }
    async handleEvent(event) {
        switch (event.type) {
            case SyncEvent.ReceivedSharedVaultInvites:
                await this.processInboundInvites(event.payload);
                break;
            case ApplicationEvent.Launched:
                if (!this.session.isSignedIn()) {
                    return;
                }
                void this.downloadInboundInvites();
                break;
            case NotificationServiceEvent.NotificationReceived:
                await this.handleNotification(event.payload);
                break;
            case WebSocketsServiceEvent.UserInvitedToSharedVault:
                await this.processInboundInvites([event.payload.invite]);
                break;
        }
    }
    async handleNotification(event) {
        switch (event.eventPayload.props.type.value) {
            case NotificationType.TYPES.SharedVaultInviteCanceled: {
                this.removePendingInvite(event.eventPayload.props.primaryIdentifier.value);
                void this.notifyEvent(VaultInviteServiceEvent.InvitesReloaded);
                break;
            }
        }
    }
    getCachedPendingInviteRecords() {
        return Object.values(this.pendingInvites);
    }
    async downloadInboundInvites() {
        const response = await this.invitesServer.getInboundUserInvites();
        if (isErrorResponse(response)) {
            return ClientDisplayableError.FromString(`Failed to get inbound user invites ${JSON.stringify(response)}`);
        }
        this.pendingInvites = {};
        await this.processInboundInvites(response.data.invites);
        return response.data.invites;
    }
    async getOutboundInvites(sharedVault) {
        const response = await this.invitesServer.getOutboundUserInvites();
        if (isErrorResponse(response)) {
            return ClientDisplayableError.FromString(`Failed to get outbound user invites ${JSON.stringify(response)}`);
        }
        if (sharedVault) {
            return response.data.invites.filter((invite) => invite.shared_vault_uuid === sharedVault.sharing.sharedVaultUuid);
        }
        return response.data.invites;
    }
    async acceptInvite(pendingInvite) {
        if (!pendingInvite.trusted) {
            return Result.fail('Cannot accept untrusted invite');
        }
        const acceptResult = await this._acceptVaultInvite.execute({
            invite: pendingInvite.invite,
            message: pendingInvite.message,
        });
        if (acceptResult.isFailed()) {
            return Result.fail(acceptResult.getError());
        }
        this.removePendingInvite(pendingInvite.invite.uuid);
        this.sync.sync().catch(console.error);
        this.vaultUsers.invalidateVaultUsersCache(pendingInvite.invite.shared_vault_uuid).catch(console.error);
        await this._decryptErroredPayloads.execute();
        await this.sync.syncSharedVaultsFromScratch([pendingInvite.invite.shared_vault_uuid]);
        return Result.ok();
    }
    async getInvitableContactsForSharedVault(sharedVault) {
        const users = await this.vaultUsers.getSharedVaultUsersFromServer(sharedVault);
        if (!users) {
            return [];
        }
        const contacts = this._getAllContacts.execute();
        if (contacts.isFailed()) {
            return [];
        }
        const outboundInvites = await this.getOutboundInvites(sharedVault);
        if (isClientDisplayableError(outboundInvites)) {
            return [];
        }
        return contacts.getValue().filter((contact) => {
            const isContactAlreadyInVault = users.some((user) => user.user_uuid === contact.contactUuid);
            const isContactAlreadyInvited = outboundInvites.some((invite) => invite.user_uuid === contact.contactUuid);
            return !isContactAlreadyInVault && !isContactAlreadyInvited;
        });
    }
    async inviteContactToSharedVault(sharedVault, contact, permission) {
        const contactsResult = await this._getVaultContacts.execute({
            sharedVaultUuid: sharedVault.sharing.sharedVaultUuid,
            readFromCache: false,
        });
        if (contactsResult.isFailed()) {
            return Result.fail(contactsResult.getError());
        }
        const contacts = contactsResult.getValue();
        const result = await this._inviteToVault.execute({
            sharedVault,
            recipient: contact,
            sharedVaultContacts: contacts,
            permission,
        });
        if (result.isFailed()) {
            return Result.fail(result.getError());
        }
        void this.notifyEvent(VaultInviteServiceEvent.InviteSent);
        await this.sync.sync();
        return result;
    }
    isVaultUserOwner(user) {
        const result = this._getVault.execute({ sharedVaultUuid: user.shared_vault_uuid });
        if (result.isFailed()) {
            return false;
        }
        const vault = result.getValue();
        return vault != undefined && vault.sharing.ownerUserUuid === user.user_uuid;
    }
    async deleteInvite(invite) {
        const response = await this.invitesServer.deleteInvite({
            sharedVaultUuid: invite.shared_vault_uuid,
            inviteUuid: invite.uuid,
        });
        if (isErrorResponse(response)) {
            return ClientDisplayableError.FromString(`Failed to delete invite ${JSON.stringify(response)}`);
        }
        this.removePendingInvite(invite.uuid);
    }
    async reprocessCachedInvitesTrustStatusAfterTrustedContactsChange() {
        const cachedInvites = this.getCachedPendingInviteRecords().map((record) => record.invite);
        await this.processInboundInvites(cachedInvites);
    }
    async processInboundInvites(invites) {
        if (invites.length === 0) {
            this.updatePendingInviteCount();
            return;
        }
        const keys = this._getKeyPairs.execute();
        if (keys.isFailed()) {
            return;
        }
        for (const invite of invites) {
            delete this.pendingInvites[invite.uuid];
            const sender = this._findContact.execute({ userUuid: invite.sender_uuid });
            if (!sender.isFailed()) {
                const trustedMessage = this._getTrustedPayload.execute({
                    payload: invite,
                    privateKey: keys.getValue().encryption.privateKey,
                    ownUserUuid: this.session.userUuid,
                    sender: sender.getValue(),
                });
                if (!trustedMessage.isFailed()) {
                    this.addPendingInvite({
                        invite,
                        message: trustedMessage.getValue(),
                        trusted: true,
                    });
                    continue;
                }
            }
            const untrustedMessage = this._getUntrustedPayload.execute({
                payload: invite,
                privateKey: keys.getValue().encryption.privateKey,
            });
            if (!untrustedMessage.isFailed()) {
                this.addPendingInvite({
                    invite,
                    message: untrustedMessage.getValue(),
                    trusted: false,
                });
            }
        }
        void this.notifyEvent(VaultInviteServiceEvent.InvitesReloaded);
    }
}
